import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { notify } from '@core/notifications/notify';
import { useModal, FrameworkAssignmentWizard } from '@components/modal';
import PageInfoButton from '@/components/PageInfoButton';
import {
  Shield,
  Download,
  CheckCircle2,
  RefreshCw,
  Library,
  ExternalLink,
  Layers,
  FileText,
  ChevronDown,
  Search,
  Loader2,
  Sparkles,
  AlertTriangle,
  Wand2,
} from 'lucide-react';

// --- Types ---

interface LibraryFramework {
  id: string;
  name: string;
  shortName: string;
  authority: string;
  version: string;
  category: string;
  description: string;
  tags: string[];
  scopeCount: number;
  requirementCount: number;
  deployed: boolean;
  deployedFrameworkId: number | null;
}

interface DeployResult {
  frameworkId: number;
  versionId: number;
  templateId: number;
  templateStatus?: 'draft' | 'published' | 'archived';
  nodesCreated: number;
  fieldsCreated: number;
  action: string;
}

// --- API helpers ---

const getCsrfToken = () =>
  document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

const API_BASE = '/api/compliance/definition/framework-library';

async function fetchLibrary(): Promise<LibraryFramework[]> {
  const res = await fetch(API_BASE, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to fetch framework library');
  const json = await res.json();
  return json.data;
}

async function deployFramework(libraryId: string): Promise<DeployResult> {
  const res = await fetch(`${API_BASE}/deploy`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ libraryId }),
  });
  if (!res.ok) throw new Error('Failed to deploy framework');
  const json = await res.json();
  return json.data;
}

async function deployAll(): Promise<DeployResult[]> {
  const res = await fetch(`${API_BASE}/deploy-all`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to deploy all frameworks');
  const json = await res.json();
  return json.data;
}

// --- Category badge styles ---

const categoryColors: Record<string, string> = {
  Security: 'bg-blue-100 text-blue-800 border-blue-200',
  Privacy: 'bg-purple-100 text-purple-800 border-purple-200',
  Healthcare: 'bg-rose-100 text-rose-800 border-rose-200',
  Financial: 'bg-amber-100 text-amber-800 border-amber-200',
  Environmental: 'bg-green-100 text-green-800 border-green-200',
  Governance: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  'Risk Management': 'bg-orange-100 text-orange-800 border-orange-200',
  Quality: 'bg-teal-100 text-teal-800 border-teal-200',
  information_security: 'bg-blue-100 text-blue-800 border-blue-200',
  privacy: 'bg-purple-100 text-purple-800 border-purple-200',
  healthcare: 'bg-rose-100 text-rose-800 border-rose-200',
  financial: 'bg-amber-100 text-amber-800 border-amber-200',
  environmental: 'bg-green-100 text-green-800 border-green-200',
  general: 'bg-slate-100 text-slate-700 border-slate-200',
};

function getCategoryStyle(category: string): string {
  return categoryColors[category] ?? 'bg-slate-100 text-slate-700 border-slate-200';
}

function formatCategory(category: string): string {
  const normalized = String(category || '').trim();
  if (!normalized) return 'General';
  return normalized
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

// --- Component ---

const FrameworkLibrary: React.FC = () => {
  const navigate = useNavigate();
  const { openModal } = useModal();
  const [frameworks, setFrameworks] = useState<LibraryFramework[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [deployingId, setDeployingId] = useState<string | null>(null);
  const [deployingAll, setDeployingAll] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('fwlib_expanded');
      return saved ? new Set(JSON.parse(saved) as string[]) : new Set<string>();
    } catch { return new Set<string>(); }
  });

  const loadLibrary = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchLibrary();
      setFrameworks(data);
      // Only expand the first item on first load
      const firstFramework = data[0];
      if (!localStorage.getItem('fwlib_expanded') && firstFramework) {
        const expanded = new Set([firstFramework.id]);
        setExpandedIds(expanded);
        localStorage.setItem('fwlib_expanded', JSON.stringify(Array.from(expanded)));
      }
    } catch (err) {
      notify.error('Failed to load framework library');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadLibrary(); }, [loadLibrary]);

  const handleDeploy = async (fw: LibraryFramework) => {
    setDeployingId(fw.id);
    try {
      const result = await deployFramework(fw.id);
      const templateStatus = result.templateStatus || 'draft';
      notify.success(
        `Deployed ${fw.shortName}: ${result.nodesCreated} nodes, ${result.fieldsCreated} fields. Opening in the builder…`,
      );
      setFrameworks((prev) =>
        prev.map((f) =>
          f.id === fw.id
            ? { ...f, deployed: true, deployedFrameworkId: result.frameworkId }
            : f,
        ),
      );
      // Jump straight into the builder with the freshly-created template
      // so the user can verify it follows the builder's pattern end-to-end
      // (nodes, fields, statuses) without hunting for it in the Templates
      // list. Builder reads ?templateId from the query string.
      navigate(`/compliance/definition/builder?templateId=${result.templateId}&frameworkId=${result.frameworkId}&versionId=${result.versionId}`);
    } catch (err) {
      notify.error(`Failed to deploy ${fw.shortName}`);
    } finally {
      setDeployingId(null);
    }
  };

  const handleDeployAll = async () => {
    setDeployingAll(true);
    try {
      const results = await deployAll();
      notify.success(`Successfully deployed ${results.length} framework(s)`);
      await loadLibrary();
    } catch (err) {
      notify.error('Failed to deploy all frameworks');
    } finally {
      setDeployingAll(false);
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      localStorage.setItem('fwlib_expanded', JSON.stringify(Array.from(next)));
      return next;
    });
  };

  // --- Filters ---

  const categories = [...new Set(frameworks.map(f => f.category))].sort();

  const filteredFrameworks = frameworks.filter(fw => {
    const matchesSearch = fw.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fw.authority.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fw.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fw.shortName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !categoryFilter || fw.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // --- Derived stats ---

  const totalFrameworks = frameworks.length;
  const deployedCount = frameworks.filter((f) => f.deployed).length;
  const availableCount = totalFrameworks - deployedCount;
  const totalRequirements = frameworks.reduce((sum, f) => sum + f.requirementCount, 0);

  // --- Loading ---

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="text-blue-600 animate-spin" size={40} />
      </div>
    );
  }

  // --- Render ---

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center">
              <h1 className="text-3xl font-bold text-slate-900 text-left">Framework Library</h1>
              <PageInfoButton />
            </div>
            <p className="text-slate-500 text-sm mt-0.5 text-left">Pre-built compliance frameworks ready for one-click deployment</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => openModal({ component: FrameworkAssignmentWizard, props: { onComplete: () => {} }, options: { size: 'large' } })} className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white font-medium text-sm rounded-lg hover:bg-indigo-700 transition-colors shadow-sm">
              <Wand2 size={16} /> Quick Assign
            </button>
            <button onClick={loadLibrary} className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-slate-700 font-medium text-sm rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
              <RefreshCw size={16} /> Refresh
            </button>
            <button
              onClick={handleDeployAll}
              disabled={deployingAll || availableCount === 0}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {deployingAll ? (
                <><Loader2 size={16} className="animate-spin" /> Deploying All...</>
              ) : (
                <><Download size={16} /> Deploy All ({availableCount})</>
              )}
            </button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider text-left">Total Frameworks</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{totalFrameworks}</p>
          </div>
          <div className="bg-white rounded-xl border border-emerald-200 p-4 bg-emerald-50/50">
            <p className="text-xs font-medium text-emerald-700 uppercase tracking-wider text-left">Deployed</p>
            <p className="text-2xl font-bold text-emerald-800 mt-1">{deployedCount}</p>
          </div>
          <div className="bg-white rounded-xl border border-amber-200 p-4 bg-amber-50/50">
            <p className="text-xs font-medium text-amber-700 uppercase tracking-wider text-left">Available</p>
            <p className="text-2xl font-bold text-amber-800 mt-1">{availableCount}</p>
          </div>
          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider text-left">Total Requirements</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{totalRequirements.toLocaleString()}</p>
          </div>
        </div>

        {/* Search + Filter */}
        <div className="flex items-center gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search by name, authority, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 text-sm"
            />
          </div>
          {categories.length > 1 && (
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{formatCategory(cat)}</option>
              ))}
            </select>
          )}
        </div>

        {/* Framework List — collapsible rows */}
        {filteredFrameworks.length === 0 ? (
          <div className="bg-white rounded-3xl border border-slate-100 p-12">
            <div className="w-20 h-20 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Library className="text-blue-500" size={40} />
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-2">No Frameworks Found</h3>
            <p className="text-slate-500 max-w-md mx-auto">
              {searchTerm || categoryFilter ? 'Try adjusting your search or filter.' : 'The framework library is empty.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredFrameworks.map(fw => {
              const isExpanded = expandedIds.has(fw.id);
              const isDeploying = deployingId === fw.id;

              return (
                <div key={fw.id} className="bg-white rounded-3xl border border-slate-100 overflow-hidden">

                  {/* Framework row */}
                  <div
                    className={`flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                      fw.deployed ? 'border-l-4 border-l-emerald-500' : 'border-l-4 border-l-blue-400'
                    }`}
                    onClick={() => toggleExpand(fw.id)}
                  >
                    {/* Expand icon */}
                    <ChevronDown size={16} className={`text-slate-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />

                    {/* Icon */}
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      fw.deployed ? 'bg-emerald-100' : 'bg-blue-100'
                    }`}>
                      <Shield size={18} className={fw.deployed ? 'text-emerald-600' : 'text-blue-600'} />
                    </div>

                    {/* Name + authority */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-slate-900 truncate text-left">{fw.name}</p>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${getCategoryStyle(fw.category)}`}>
                          {formatCategory(fw.category)}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 text-left">{fw.authority} &middot; v{fw.version}</p>
                    </div>

                    {/* Status badge */}
                    {fw.deployed ? (
                      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md font-bold text-[10px] bg-emerald-100 text-emerald-800 border border-emerald-200">
                        <CheckCircle2 size={12} /> Deployed
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md font-bold text-[10px] bg-blue-100 text-blue-800 border border-blue-200">
                        <Sparkles size={12} /> Available
                      </span>
                    )}

                    {/* Counts */}
                    <span className="text-xs text-slate-400 flex-shrink-0">{fw.scopeCount} scopes</span>
                    <span className="text-xs text-slate-400 flex-shrink-0">{fw.requirementCount} reqs</span>

                    {/* Deploy/View action — hidden when expanded (shown inside panel instead) */}
                    {!isExpanded && (
                      <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                        {fw.deployed ? (
                          <button
                            onClick={() => navigate(`/compliance/definition/builder?frameworkId=${fw.deployedFrameworkId}`)}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors"
                            title="Open deployed template in the builder"
                          >
                            <ExternalLink size={13} /> Open in Builder
                          </button>
                        ) : (
                          <button
                            onClick={() => handleDeploy(fw)}
                            disabled={isDeploying || deployingAll}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm disabled:opacity-50"
                            title="Deploy as a draft template and open in the builder"
                          >
                            {isDeploying ? (
                              <><Loader2 size={13} className="animate-spin" /> Deploying...</>
                            ) : (
                              <><Download size={13} /> Deploy &amp; Open</>
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Expanded detail panel */}
                  {isExpanded && (
                    <div className="border-t border-slate-100 bg-slate-50/50">
                      <div className="ml-14 mr-5 my-3 space-y-3">
                        {/* Description */}
                        <p className="text-sm text-slate-600 leading-relaxed">{fw.description}</p>

                        {/* Stats row */}
                        <div className="grid grid-cols-3 gap-3">
                          <div className="bg-white border border-slate-200 rounded-lg px-4 py-3">
                            <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                              <Layers size={12} /> Scopes
                            </div>
                            <p className="text-sm font-semibold text-slate-800">{fw.scopeCount}</p>
                          </div>
                          <div className="bg-white border border-slate-200 rounded-lg px-4 py-3">
                            <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                              <FileText size={12} /> Requirements
                            </div>
                            <p className="text-sm font-semibold text-slate-800">{fw.requirementCount}</p>
                          </div>
                          <div className="bg-white border border-slate-200 rounded-lg px-4 py-3">
                            <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                              <Shield size={12} /> Short Name
                            </div>
                            <p className="text-sm font-semibold text-slate-800">{fw.shortName}</p>
                          </div>
                        </div>

                        {/* Tags */}
                        {fw.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {fw.tags.map((tag) => (
                              <span
                                key={tag}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Deployed hint — primary action is now Open in Builder
                            so the user can verify the framework follows the
                            builder pattern end-to-end (nodes, fields, statuses).
                            Templates list stays as a secondary link for users
                            who want to browse all templates from there. */}
                        {fw.deployed && (
                          <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2.5">
                            <span className="text-sm text-emerald-800 font-medium">Deployed — framework + active version + draft template created</span>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => navigate('/compliance/definition/templates')}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-white border border-emerald-300 hover:bg-emerald-50 rounded-lg transition-colors"
                                title="Browse all templates"
                              >
                                Templates
                              </button>
                              <button
                                onClick={() => navigate(`/compliance/definition/builder?frameworkId=${fw.deployedFrameworkId}`)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors shadow-sm"
                                title="Open the deployed template in the builder to verify nodes, fields, and statuses"
                              >
                                <ExternalLink size={13} /> Open in Builder
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Available — deploy action inside panel.
                            Deploy creates the framework + active version +
                            draft template and immediately navigates the user
                            into the builder so they can verify the structure
                            against the builder's rules before publishing. */}
                        {!fw.deployed && (
                          <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5">
                            <span className="text-sm text-blue-800 font-medium">
                              Creates a <strong>draft template</strong> you can fully edit. Nothing is locked or published until you explicitly click <em>Publish</em> in the builder. You'll land in the builder right after.
                            </span>
                            <button
                              onClick={() => handleDeploy(fw)}
                              disabled={isDeploying || deployingAll}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm disabled:opacity-50 whitespace-nowrap"
                              title="Deploy as a draft template and open in the builder for review"
                            >
                              {isDeploying ? (
                                <><Loader2 size={13} className="animate-spin" /> Deploying...</>
                              ) : (
                                <><Download size={13} /> Deploy &amp; Open in Builder</>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default FrameworkLibrary;
